import React, { useEffect, useRef, useState, useCallback } from "react";
import "@innovatrics/dot-document-auto-capture";
import { CameraIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import Head from "../Head";
import Footer from "../../components/Footer";
import { useContext } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import { languageData } from "../../constants/languageData";
import { getDocumentCountry, getDocumentIdType, getDocumentFieldName } from "../../utils/documentFields";
import focusIcon from "../../assets/focus.svg";
import DocumentUI from "./DocumentUI";
import DocumentCamera from "./DocumentCamera";
import { residentIDCardName, drivingLicenceName, idCardName, countries } from "../../constants/countries";
import ImageCheck from "./ImageCheck";
import "@innovatrics/dot-document-auto-capture/events";
import "./docCapture.css";
import uploadFile from "../../assets/upload_file.svg";
import { useParams } from "react-router-dom";
import JourneyConfig from "../../context/JourneyConfig";
import Loader from "../../components/Loader/Loader";
import CameraLoadingOverlay from "../../components/Loader/CameraLoadingOverlay";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import "../../styles/Modal.css";
import { UploadStatusMessages } from "../../constants/failedUpload";
import captureDocButton from "../../assets/captureDocButton.svg";
import MobileCapture from "./MobileCapture";
import GoogleIcon from "../../components/GoogleIcon";
import { arrayBufferToBase64 } from "../../utils/arrayBufferUtility";

const MobileFrontCapture = ({
  stepperFunc,
  steps,
  docIteration,
  currentDocumentRetries,
  isFirstDocumentVerificationDone,
  frontImageRef,
  currentDocumentStep,
  currentStep,
  capturingMode,
  setCapturingMode,
  setAutoCaptureLoadingTime,
}) => {
  const instructionChangeEvent = "document-auto-capture:instruction-changed";
  const { config } = useContext(GlobalConfig);
  const language = sessionStorage.getItem("language") ?? "en";
  const { journey, setJourney, getJourney } = useContext(JourneyConfig);
  const [imageUrl, setImageUrl] = useState("");
  const [loadTiming, setLoadTiming] = useState(performance.now());
  const innovatricsCameraRef = useRef(null);
  const [captureStatus, setCaptureStatus] = useState("document_not_present");
  const [isLoading, setIsLoading] = useState(false);
  const { journeyId } = useParams();
  const documentFailureReason = sessionStorage.getItem('documentFailureReason');
  const [sameSideDocumentFlagNumber, setSameSideDocumentFlagNumber] = useState(
    documentFailureReason
      ? documentFailureReason === "ONLY_FRONT"
        ? 1
        : documentFailureReason === "ONLY_BACK"
          ? 2
          : -1
      : -1
  );
  const isMobile = useDeviceDetection();
  const { branding } = config ?? {};
  const documentCapture = Array.isArray(config?.documentCapture)
    ? config?.documentCapture[currentDocumentStep]
    : config?.documentCapture;
  const { capturedData: journeyData } = journey ?? {};
  const color = config?.branding?.accentColor;
  const bgColor =
    "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";
  const env = process.env.NODE_ENV || "development";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraRunning, setIsCameraRunning] = useState(false);
  const timeoutRef = useRef(null);
  const streamRef = useRef(null);
  const [message, setMessage] = useState("");
  const videoRef = useRef(null); // Reference to the video element
  const canvasRef = useRef(null); // Reference to the canvas element
  const [facingMode, setFacingMode] = useState(
    isMobile ? "environment" : "user"
  );
  const [width, setWidth] = useState(window.innerWidth);
  const [captureMode, setCaptureMode] = useState("auto");
  const fileInputRef = useRef(null);
  // This document's own country and type, not the first document's (IDX-677).
  const [countryCode] = useState(() => getDocumentCountry(journey, currentDocumentStep));
  const [countryName] = useState(
    countryCode
      ? countries?.find((country) => country.value === countryCode)?.label
      : null
  );
  const [documentName] = useState(() => getDocumentIdType(journey, currentDocumentStep));
  const isIOS = useCallback(() => /iPad|iPhone|iPod/.test(navigator.userAgent), []);
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);

  useEffect(() => {
    const cleanupCameras = async () => {
      try {
        sessionStorage.removeItem('isCameraPage');
        const video = videoRef?.current;
        stopSafeStreamFromRef();
        console.log('[MobileFrontCapture][cleanupCameras] stream cleared without error')
        if (video?.srcObject) {
          video.srcObject = null;
        }
        if (isIOS()) {
          await new Promise(resolve => setTimeout(resolve, 300));
          console.log('[MobileFrontCapture][cleanupCameras]: Nulling iOS srcObject (twice)');
          if(video?.srcObject) video.srcObject = null;
          if(video?.srcObject) video.srcObject = null;
        }
      } catch (error) {
        console.log("[MobileFrontCapture][cleanupCameras] Error during clean-up : ",error)
      }
    };
    sessionStorage.setItem('isCameraPage', 'true');
    return () => {
      cleanupCameras().catch(error => {
        console.error('Cleanup error:', error);
      });
    };
  }, []); 

  const stopSafeStreamFromRef = useCallback(() => {
    console.log('[MobileFrontCapture][safeStream] Attempting to stop stream from streamRef');
  
    const stream = streamRef?.current;
    if (!stream) {
      console.log('[MobileFrontCapture][safeStream] No stream found in streamRef - skipping');
      return;
    }
  
    try {
      const tracks = stream.getTracks();
      console.log(`[MobileFrontCapture][safeStream] Found ${tracks.length} tracks to stop`);
  
      tracks.forEach((track) => {
        console.log(`[MobileFrontCapture][safeStream] Stopping ${track?.kind} track (ID: ${track?.id})`);
        track.stop();
        console.log(`[MobileFrontCapture][safeStream] Track stopped: ${track?.readyState}`);
      });
  
      streamRef.current = null;
      console.log('[MobileFrontCapture][safeStream] Stream cleanup complete and ref cleared');
    } catch (e) {
      console.error('[MobileFrontCapture][safeStream] Stream cleanup failed:', e);
    }
  }, []);  

  useEffect(() => {
    if (isMobile) {
      setFacingMode("environment");
    }
  }, [isMobile]);

  useEffect(() => {    
    // Set up 5-second fallback
    timeoutRef.current = setTimeout(() => {
      if (!isCameraRunning) {
        console.log('[MobileFrontCapture][CameraReadyState] ⏱️ Fallback triggered - marking camera as ready after timeout');
        setIsCameraRunning(true);
      }
    }, 10000);
  
    const handleStateChanged = (event) => {
      if (!event.detail) {
        console.warn('[MobileFrontCapture][CameraReadyState] Received state change event without details', event);
        return;
      }
  
      console.log(`[MobileFrontCapture][CameraReadyState] State changed: ${event.detail.appState}`, JSON.stringify(event.detail));
      
      if (event.detail.appState === 'RUNNING') {
        const durationInSeconds = ((performance.now() - loadTiming) / 1000).toFixed(2);
        setAutoCaptureLoadingTime(durationInSeconds);
        console.log('[MobileFrontCapture][CameraReadyState] 🎥 Camera is now operational');
        if(innovatricsCameraRef.current){
          console.log('[MobileFrontCapture][CameraReadyState] current stream set to innov. camera stream');
          streamRef.current = innovatricsCameraRef.current?.getVideoElement();
        }
        setIsCameraRunning(true);
        
        if (timeoutRef.current) {
          console.log('[MobileFrontCapture][CameraReadyState] Clearing fallback timeout');
          clearTimeout(timeoutRef.current);
        }
      } else if (event?.detail?.appState === 'ERROR') {
        console.log('[MobileFrontCapture][CameraReadyState] Error occured during camera initialisation . setting cameraRunning state to true after 2 second delay so that other buttons should be enabled');
        
        // Set cameraRunning to true after 2 seconds if it's still false
        setTimeout(() => {
          if (!isCameraRunning) {
            console.log('[MobileFrontCapture][CameraReadyState] 2 second delay completed, setting cameraRunning to true');
            setIsCameraRunning(true);
          } else {
            console.log('[MobileFrontCapture][CameraReadyState] 2 second delay completed, but cameraRunning is already true, skipping');
          }
        }, 2000);
      }
    };
  
    console.log('[MobileFrontCapture][CameraReadyState] Adding event listener...');
    document.addEventListener(
      'document-auto-capture:state-changed', 
      handleStateChanged
    );
  
    return () => {
      console.log('[MobileFrontCapture][CameraReadyState] Cleaning up...');
      if (timeoutRef.current) {
        console.log('[MobileFrontCapture][CameraReadyState] Clearing pending timeout');
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener(
        'document-auto-capture:state-changed',
        handleStateChanged
      );
    };
  }, []);
 
  
  const startManualCamera = useCallback(async () => {
    console.log('[MobileFrontCapture][startManualCamera]: Starting manual camera initialization');
    setIsSwitchingMode(true);
    
    try {
      const constraints = {
        video: {
          facingMode,
          ...(isMobile ? {
            height: { ideal: 1080 },
            width: { ideal: 1920 }
          } : {})
        }
      };
      console.log('[MobileFrontCapture][startManualCamera]: Using constraints:', JSON.stringify(constraints));
  
      let attempts = 0;
      const maxAttempts = 2;
      
      while (attempts < maxAttempts) {
        try {
          console.log(`[MobileFrontCapture][startManualCamera]: Attempt ${attempts + 1}/${maxAttempts}`);
          console.log('[MobileFrontCapture][startManualCamera]: Stopping any existing webcam stream');
          stopSafeStreamFromRef();

          if(!videoRef || !videoRef.current){
            console.log('[MobileFrontCapture][startManualCamera]: VideoRef not defined till now');
          }
          
          if (isIOS()) {
            // console.log('[MobileFrontCapture][startManualCamera]: Applying 500ms iOS delay');
            // await new Promise(resolve => setTimeout(resolve, 500));
            console.log('[MobileFrontCapture][startManualCamera]: Applying iOS specific settings for quick stream attachment');
            videoRef.current.playsInline = true;
            videoRef.current.muted = true;
            videoRef.current.setAttribute('playsinline', '');
            videoRef.current.setAttribute('webkit-playsinline', '');
            videoRef.current.style.transform = 'translateZ(0)'; // Force hardware acceleration

            // Small delay to allow DOM updates
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          console.log('[MobileFrontCapture][startManualCamera]: Requesting camera access');
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          console.log('[MobileFrontCapture][startManualCamera]: Camera access granted, attaching stream');
          
          videoRef.current.srcObject = stream;
          console.log('[MobileFrontCapture][startManualCamera] current stream set to manual camera stream');
          streamRef.current = stream;

          videoRef.current.onloadedmetadata = () => {
            console.log(`[MobileFrontCapture][startManualCamera]: Current video resolution for manual-capture stream: ${videoRef.current.videoWidth}x${videoRef.current.videoHeight}`);
            setIsSwitchingMode(false);
          };

          if (isIOS()) {
            console.log('[MobileFrontCapture][startManualCamera]: iOS detected - handling playback');
            
            try {
              await videoRef.current.play();
              console.log('[MobileFrontCapture][startManualCamera]: iOS video playback started');
            } catch (e) {
              console.log('[MobileFrontCapture][startManualCamera]: iOS play error:', e);
              // Try again with a small delay
              await new Promise(resolve => setTimeout(resolve, 200));
              await videoRef.current.play();
            }
          }
          
          console.log('[MobileFrontCapture][startManualCamera]: Manual camera started successfully');
          return;
        } catch (error) {
          attempts++;
          console.error(`[MobileFrontCapture][startManualCamera]: Attempt ${attempts} failed:`, error);
          if (attempts >= maxAttempts) {
            console.log('[MobileFrontCapture][startManualCamera]: Max attempts reached - throwing error');
            return;
          }
          const delay = 300 * attempts;
          console.log(`[MobileFrontCapture][startManualCamera]: Waiting ${delay}ms before retry`);
        }
      }
    } catch (error) {
      console.error('[MobileFrontCapture][startManualCamera]: Manual camera failed:', error);
    } finally {
      console.log('[MobileFrontCapture][startManualCamera]: Finished manual camera initialization attempt');
    }
  }, [facingMode]);

  useEffect(() => {
    const handleModeChange = async () => {
      console.log(`[MobileFrontCapture][Mode Change] Current mode: ${captureMode}`);
      
      if (captureMode === 'manual') {
        console.log('[MobileFrontCapture][Mode Change] Starting switch to manual mode');
        try {
          // 1. Stop auto capture
          // console.log('[MobileFrontCapture][Mode Change] Stopping auto capture...');
          // stopSafeStreamFromRef();
          // console.log('[MobileFrontCapture][Mode Change] Auto capture stopped successfully');
          
          // 2. Verify video element is ready
          console.log('[MobileFrontCapture][Mode Change] Checking video element...');
          if (!videoRef.current) {
            console.log('[MobileFrontCapture][Mode Change] Video ref not available');
            throw new Error('Video ref not available');
          }
          if (window.getComputedStyle(videoRef.current).display === 'none') {
            console.log('[MobileFrontCapture][Mode Change] Video element is hidden');
            throw new Error('Video element is hidden');
          }
          console.log('[MobileFrontCapture][Mode Change] Video element ready:', videoRef.current);
  
          // 3. Start manual camera
          console.log('[MobileFrontCapture][Mode Change] Starting manual camera...');
          await startManualCamera();
          console.log('[MobileFrontCapture][Mode Change] Manual camera started successfully');
          
        } catch (error) {
          console.error('[MobileFrontCapture][Mode Change] Error during switch:', error);
          console.log('[MobileFrontCapture][Mode Change] Reverting to auto mode');
          stopSafeStreamFromRef();
          setCaptureMode('auto');
          
          // Additional error handling if needed
          throw error;
        } finally {
          console.log('[MobileFrontCapture][Mode Change] Finalizing mode switch');
        }
      }else{
        const innovatricsElement = document.getElementById('x-dot-document-auto-capture');
        const videoContainer = innovatricsElement?.firstElementChild;
        if (videoContainer) {
          const videoElement = videoContainer.querySelector('video');
          if (videoElement?.srcObject) {
            console.log(`[MobileFrontCapture][Camera] Current video resolution for auto-capture stream: ${videoElement.videoWidth}x${videoElement.videoHeight}`);
          }
        }
      }
    };
  
    console.log('[MobileFrontCapture][Mode Change] Triggering mode change handler');
    handleModeChange().catch(error => {
      console.error('[MobileFrontCapture][Mode Change] Unhandled error in mode change:', error);
    });
  }, [captureMode]);

  useEffect(() => {
    if (journeyId && !journey) getJourney(journeyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  const handleFileChange = async (e) => {
    try {
      console.log('[MobileFrontCapture][File Upload] File selection triggered');
  
      const file = e.target.files[0];
      
      if (!file) {
        console.warn('[MobileFrontCapture][File Upload] No file selected');
        return;
      }
  
      // Minimal file info logging (avoid size/lastModified)
      console.log('[MobileFrontCapture][File Upload] Selected file:', {
        name: file.name,
        type: file.type
      });
      setCapturingMode("UPLOAD");
      
      // 1. Validate file type first (doesn't trigger metadata checks)
      // India supports PDF uploads, same as other IDs.
      const allowedExtensions = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
      if (!allowedExtensions.includes(file.type)) {
        const errorMsg = `Invalid file type: ${file.type}. Allowed: ${allowedExtensions.join(', ')}`;
        console.error('[MobileFrontCapture][File Upload]', errorMsg);
        setMessage(
          languageData[language]?.failedUpload?.text ||
          UploadStatusMessages.FailedUploadText
        );
        setIsModalOpen(true);
        e.target.value = '';
        return;
      }
  
      // 2. Read file content immediately
    const buffer = await file.arrayBuffer();

    // 3. Validate size from buffer (not original file)
    const maxSize = 5 * 1024 * 1024;
    if (buffer.byteLength > maxSize) {
      const sizeInMB = (buffer.byteLength / (1024 * 1024)).toFixed(2);
      const errorMsg = `File too large: ${sizeInMB}MB. Max allowed: 5MB`;
      console.error('[MobileFrontCapture][File Upload]', errorMsg);
      setMessage(
        languageData[language]?.failedUpload?.text ||
        UploadStatusMessages.FailedUploadText
      );
      setIsModalOpen(true);
      e.target.value = '';
      return;
    }

    // 4. Process valid file
    const base64Data = arrayBufferToBase64(buffer);
    console.log('[MobileFrontCapture][File Upload] File successfully converted to base64, length:', base64Data.length);
    setImageUrl(`data:${file.type};base64,${base64Data}`);
    e.target.value = '';
  
    } catch (error) {
      console.error('[MobileFrontCapture][File Upload] Unexpected error:', {
        name: error?.name,
        message: error.message,
        userAgent: navigator?.userAgent
      });
      e.target.value = '';
      // No setMessage here - silent failure
    }
  };  

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleDocumentPhotoTaken = async ({ image, data }, content) => {
    setCapturingMode("AUTO")
    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result;
      console.log('[MobileFrontCapture][Camera] Image successfully converted to base64, length:', base64Data.length);
      setImageUrl(base64Data);
    };
    reader.readAsDataURL(image);
  };

  const handleBackButtonClick = () => {
    setJourney({
      ...journey,
      capturedData: { ...journey?.capturedData, [getDocumentFieldName("idType", currentDocumentStep)]: "" },
    });
    stepperFunc(steps[2]);
  };

  useEffect(() => {
    if (isMobile) return;

    const handleInstructionChange = (event) => {
      setCaptureStatus(event.detail.instructionCode);
    };

    document.addEventListener(instructionChangeEvent, handleInstructionChange);

    return () => {
      document.removeEventListener(
        instructionChangeEvent,
        handleInstructionChange
      );
    };
  }, []);

  useEffect(() => {
    if (imageUrl) {
      
      frontImageRef.current=imageUrl;
      console.log('[MobileFrontCapture] frontImage ref set, navigating to ImageCheck');
      stepperFunc(steps[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

    // Enhanced captureImage with error handling
  const captureImage = useCallback(async () => {
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (!video || !canvas) {
        console.log('Video or canvas element not found');
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      setCapturingMode("MANUAL");
      await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create image blob'));
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const base64Data = reader.result;
            console.log('[MobileFrontCapture][Manual Capture] Blob successfully converted to base64, length:', base64Data.length);
            setImageUrl(base64Data);
            resolve();
          };
          reader.readAsDataURL(blob);
        }, "image/png");
      });

    } catch (error) {
      console.log("Failed to capture image. Please try again : ",error)
    }
  }, [frontImageRef]);


  if (!imageUrl && isLoading) return <Loader />;

  return (
    <>
      <>
        <Head
          countryCode={countryCode}
          showCountry
          showBackIcon={
            (
              (!isCameraRunning || isSwitchingMode) 
              || (
                (journey?.userInfo?.id?.[getDocumentFieldName("country", currentDocumentStep)]) 
                && journey?.userInfo?.id?.[getDocumentFieldName("idType", currentDocumentStep)]
              )
            ) 
            ? false 
            : true
          }          
          showLanguages={false}
          handleBack={handleBackButtonClick}
          stepperFunc={stepperFunc}
          showTooltipOption={currentDocumentRetries != 0 ? true : false}
          showMobileText
          style={{ zIndex: "100", position: "relative", top: 0 }}
        />
        <div className="parent-container" data-testid="mobile-front-capture" style={{ paddingTop: 0 }}>
          <div className="main">
            <div
              className="left-section"
              style={{ flexDirection: "column", background: "black" }}
            >
              <CameraLoadingOverlay isVisible={!isCameraRunning} />  
              <div
                style={{ 
                  position: "relative", 
                  marginBottom:40, 
                  display: captureMode == "manual" ? "block" : "none" 
                }}
                className="auto-capture"
              >
                <div
                  className="video-container"
                  style={{
                    width: "100%",
                    height:
                      width <= 767
                        ? "100%"
                        : "auto" /* Set container height as desired */,
                    overflow: "hidden",
                    marginBottom:20,
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "cover",
                      transform:
                        facingMode === "environment"
                          ? "none"
                          : "scaleX(-1)",
                    }}
                  />
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </div>

                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      borderColor: "white",
                      borderWidth: 2,
                      height: 200,
                      width: 300,
                      marginBottom: "20px",
                    }}
                  ></div>
                </div>
              </div>
              
              {captureMode === "auto" && (
                <div
                  style={{ position: "relative" }}
                  className="auto-capture"
                >
                  <div
                    className="video-container"
                    style={{
                      width: "100%",
                      height:
                        width <= 767
                          ? "100%"
                          : "auto" /* Set container height as desired */,
                      overflow: "hidden",
                    }}
                  >
                    <DocumentCamera
                      ref={innovatricsCameraRef}
                      cameraFacing={"environment"}
                      onPhotoTaken={handleDocumentPhotoTaken}
                      placeholder="id-solid"
                      backdropColor="none"
                      thresholds={{
                        brightnessHighThreshold: 0.9,
                        brightnessLowThreshold: 0.2,
                        confidenceThreshold: 0.92,
                        hotspotsScoreThreshold: 0.008,
                        outOfBoundsThreshold: 0.017,
                        sharpnessThreshold: 0.75,
                        sizeSmallThreshold: 0.45
                      }}  
                    />
                    <DocumentUI
                      theme={{
                        colors: {
                          placeholderColor: "white",
                          instructionColor: "white", 
                          instructionColorSuccess: "#00FF83",
                        },
                      }}
                      placeholder="id-solid"
                      backdropColor="rgba(10, 10, 10, 0.5)"
                      instructions={{}}
                      appStateInstructions={{
                        loading: {
                          text: "",
                          visible: false,
                        },
                      }}
                    />
                  </div>
                </div>
              )}
              {width > 767 &&
                (captureMode === "auto" ? (
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: "center",
                      position: "absolute",
                      bottom: "40px",
                    }}
                  >
                    <div style={{ color: "#919191" }}>{languageData[language]?.mobileCapture?.stillNotWorking ?? "Still not working?"}</div>
                    <div
                      style={{
                        textDecorationLine: "underline",
                        color: "white",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setCaptureMode("manual");
                      }}
                    >
                      {languageData[language]?.mobileCapture?.tryClickToCapture ?? "Try click-to-capture instead"}
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={async () => {
                      await captureImage();
                    }}
                    style={{
                      position: "absolute",
                      bottom: 24,
                      cursor: "pointer",
                    }}
                  >
                    <img src={captureDocButton} alt="button_link" />
                  </div>
                ))}
            </div>
            <div
              className="right-section md:items-start items-center"
              style={{
                // background: width <= 767 ? "#000000" : "white",
                // color: width > 767 ? "#000000" : "white",
                paddingTop: width <= 767 ? "18px" : "32px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {width > 767 ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div className="flex flex-col md:mt-[80px]">
                      <div className="flex justify-center md:justify-start">
                        <svg
                          width="44"
                          height="44"
                          viewBox="0 0 44 44"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="22"
                            cy="22"
                            r="22"
                            fill={bgColor}
                            style={{ background: bgColor }}
                          />
                          <g clip-path="url(#clip0_1556_28470)">
                            <path
                              d="M15 25.5H13V31.5H19V29.5H15V25.5ZM15 15.5H19V13.5H13V19.5H15V15.5ZM31 13.5H25V15.5H29V19.5H31V13.5ZM29 29.5H25V31.5H31V25.5H29V29.5ZM22 18.5C19.79 18.5 18 20.29 18 22.5C18 24.71 19.79 26.5 22 26.5C24.21 26.5 26 24.71 26 22.5C26 20.29 24.21 18.5 22 18.5ZM22 24.5C20.9 24.5 20 23.6 20 22.5C20 21.4 20.9 20.5 22 20.5C23.1 20.5 24 21.4 24 22.5C24 23.6 23.1 24.5 22 24.5Z"
                              fill="#1658aa"
                              style={{ fill: color }}
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_1556_28470">
                              <rect
                                width="24"
                                height="24"
                                fill="white"
                                transform="translate(10 10.5)"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                      <div
                        className="mt-6 md:mt-4 text-[18px] md:text-[26px] font-semibold text-[#1658aa] text-center md:text-left mb-6 md:mb-0"
                        style={{ color: color }}
                      >
                        {
                            (sameSideDocumentFlagNumber === 1
                                ? languageData[language]?.backDocCapture?.heading
                                : languageData[language]?.frontDocCapture?.heading)
                            +
                            (
                              documentName == 'Resident Id Card' 
                                  ? (residentIDCardName?.[countryCode] ? residentIDCardName?.[countryCode] : "Identity Card")
                                  : documentName == 'Passport'
                                  ? `${countryName} Passport`
                                  : documentName == 'Driving License'
                                  ? (drivingLicenceName?.[countryCode] ? drivingLicenceName?.[countryCode] : documentName)
                                  : documentName == 'Id Card'
                                  ? (idCardName?.[countryCode] ? idCardName?.[countryCode] : documentName)
                                  : documentName
                            )
                           +
                            languageData[language]?.frontDocCapture?.headingBack
                        }
                      </div>
                    </div>
                  </div>
                  {documentCapture?.uploadAllowed && (
                    <div className="flex flex-col md:justify-end items-center md:gap-[12px] w-full md:mt-24">
                      <div
                        style={{
                          color: "#919191",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {"jpeg, jpg, png, pdf under 5 mb"}
                      </div>
                      <button
                        className="flex items-center justify-center  gap-[8px] w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium mt-3 md:mt-[0px]"
                        style={{
                          borderColor: color,
                          borderWidth: 1,
                          color: color,
                        }}
                        onClick={() => fileInputRef.current.click()}
                      >
                        or, Upload ID{" "}
                        <GoogleIcon
                          iconName="upload"
                          size={18}
                          style={{ color: color }}
                        />
                      </button>
                    </div>
                  )}
                  <Footer />
                </div>
              ) : (
                <MobileCapture
                  currentDocumentStep={currentDocumentStep}
                  uploadAllowed={documentCapture?.uploadAllowed}
                  color={color}
                  journey={journey}
                  sameSideDocumentFlagNumber={sameSideDocumentFlagNumber}
                  docType="front"
                  isCameraRunning={isCameraRunning}
                  isSwitchingMode={isSwitchingMode}
                  captureMode={captureMode}
                  switchToManual={() => {
                    setCaptureMode("manual");
                  }}
                  captureClick={async () => {
                    await captureImage();
                  }}
                  uploadClick={() => {
                    try {
                      if (!fileInputRef.current) {
                        console.error("[MobileFrontCapture][uploadClick] File input ref is null!");
                        return;
                      }
                      console.log("[MobileFrontCapture][uploadClick]Triggering file input...");
                      fileInputRef.current.click();
                    } catch (error) {
                      console.error("[MobileFrontCapture][uploadClick] Failed to open file picker(Format-1):", (JSON.stringify(error)));
                      console.error("[MobileFrontCapture][uploadClick] Failed to open file picker(Format-2):",JSON.stringify({
                        name: error?.name,
                        message: error?.message,
                        stack: error?.stack
                      }));
                    
                    }
                  }} 
                  skipClick={() => {}}
                />
              )}
            </div>
          </div>
        </div>
      </>

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <GoogleIcon
                iconName="cancel"
                size={40}
                style={{ color: "#F14437" }}
                className="mb-4"
              />
            </div>
            <div>
              {languageData[language]?.failedUpload?.title ||
                UploadStatusMessages.FailedUploadTitle}
            </div>
            <p className="mt-4">{message}</p>
            <button
              className="px-8 py-4 mt-12 md:mt-20"
              style={{
                border: "1px solid rgba(0, 0, 0, 0.10)",
                position: "relative",
                borderRadius: 4,
                width: "100%",
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "center",
                background: color,
                color: "white",
              }}
              onClick={handleClose}
            >
              {languageData[language]?.failedUpload?.btn ||
                UploadStatusMessages.FailedUploadButton}
            </button>
          </div>
        </div>
      )}

      {/* upload file */}
      <div style={{ 
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0
      }}>
        <input
          type="file"
          ref={fileInputRef}
          accept=".jpg, .png, .jpeg, .pdf"
          onChange={handleFileChange}
        />
      </div>
    </>
  );
};

export default React.memo(MobileFrontCapture);
